import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dropdown, Flex, Modal } from 'antd'
import {
  DownOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { getRequest, postRequest } from '../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../context/global_context'
import { downloadErrorLogs } from '../../utils/common'
import { Success, Warning } from './helperComponents'
import { useIntl } from 'react-intl'
import { notifyError } from '../../utils/notifications'

const DropdownActions = ({ fileType, uploadState, reloadData }) => {
  const intl = useIntl()
  const [navState, dispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const { vehicle } = useParams()

  const MissingLogOption = {
    value: 'log_missing_components',
    labelId: 'importers.logMissingComponents',
  }

  const selectedVersion = uploadState?.selected_version
  const disabled = uploadState?.file_type !== fileType

  const selected = selectedVersion?.value

  const importFileId = selected && selected !== 'all' ? selectedVersion?.id : ''
  // console.log(fileType, uploadState)

  const disabledDownloadLogs = !(
    fileType === uploadState?.file_type && 
    selectedVersion?.file?.has_error
  ) ? true : false

  const disabledDownload = !(
    fileType === uploadState?.file_type &&
    (selectedVersion?.id ||
      [
        'all',
        'current_state_inlcude_not_constrained',
        'current_state_include_part_number',
      ].includes(selectedVersion?.value)) &&
    (!selectedVersion?.file?.error_reason)
  )

  const disabledRollback = !(
    fileType === uploadState?.file_type && 
    (selectedVersion?.id) && 
    (!selectedVersion?.file?.error_reason)
  )
  
  const downloadPath = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'importers.downloadingData' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()

    formData.append('vehicle_id', vehicle)

    if (selectedVersion?.id) {
      formData.append('importer_id', selectedVersion.id)
    }

    formData.append('file_type', uploadState?.file_type)

    if (!selectedVersion?.value?.id) {
      formData.append('selected_version', selectedVersion?.value)
    }

    const res = await postRequest('/api/importer/export/', formData, true)

    // Was `if (res?.msg)` - a blob download whose failure test depended on the
    // body carrying a message, so a failure without one fell through to the
    // success path. Same guard as the other exports.
    if (res?.status === false || !(res instanceof Blob)) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'importers.downloadError' }),
        },
      })
      return
    }

    const url = window.URL.createObjectURL(new Blob([res]))

    const link = document.createElement('a')

    link.href = url

    if (uploadState?.file_type === 'connector_layout') {
      link.setAttribute(
        'download',
        `${navState.make_model_id}_${vehicle}_${uploadState.file_type}${
          selectedVersion?.value === 'current_state_include_part_number'
            ? '_PN'
            : ''
        }.zip`
      )
    } else {
      link.setAttribute(
        'download',
        `${navState.make_model_id}_${vehicle}_${uploadState.file_name}`
      )
    }

    document.body.appendChild(link)
    link.click()
    link.remove()

    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'importers.fileDownloaded' }),
        loader: true,
      },
    })
  }

  const downloadLogs = async () => {
    const res = await getRequest(
      `/api/importer/download_logs/?download_id=${selectedVersion?.id}`
    )

    // Without this a failed request falls through to the "Logs not available"
    // modal below, which blames the data rather than the request.
    if (res?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'importers.logsDownloadError' }))
      return
    }

    const data = res?.data

    if (data?.error?.length > 0) {
      downloadErrorLogs(data.error)
    } else if (data) {
      downloadErrorLogs([data])
    } else {
      // window.alert('Logs not available.')
      modal.info({
        icon: <ExclamationCircleOutlined />,
        title: intl.formatMessage({ id: 'forms.info' }),
        content: (
          <>
            {intl.formatMessage({ id: 'importers.logsNotAvailable' })}
          </>
        ),
        centered: true,
        maskClosable: true,
        onOk: () => {},
      })
    }
  }

  const downloadMissingComponent = async () => {
    const res = await postRequest('/api/dtcs/missing_component/', {
      vehicle,
      version: importFileId,
    })

    if (res?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'importers.loadError' }))
      return
    }

    if (res.data.dtcs_review?.length > 0) {
      downloadErrorLogs(res.data.dtcs_review, 'dtc_needs_to_review.txt')
    }

    if (res.data.missing_connector_device?.length > 0) {
      downloadErrorLogs(
        res.data.missing_connector_device,
        'missing_components.txt'
      )
    }

    if (
      res.data.dtcs_review?.length < 1 &&
      res.data.missing_connector_device?.length < 1
    ) {
      window.alert(intl.formatMessage({ id: 'importers.logsNotAvailable' }))
    }
  }

  const confirmRollback = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'importers.deletingVersion' }, { version: selectedVersion?.label }),
        showLoader: true,
      },
      notification: {},
    })

    const res = await getRequest(
      `/api/importer/rollback/?vehicle_id=${vehicle}&file_type=${fileType}&version=${selectedVersion?.file?.version}`
    )

    // `error_code` is backend-supplied: a network failure carries no such field,
    // so the else below fired and reported SUCCESS for a failed request.
    if (res?.status === false) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'importers.rollbackError' }),
          loader: false,
        },
      })
      return
    }

    if (res?.error_code) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'importers.rollbackError' }),
          loader: false,
        },
      })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'importers.deleteSuccess' }, { version: selectedVersion?.label }),
          loader: false,
        },
      })

      reloadData()
    }
  }

  const showRollbackConfirm = () => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'importers.rollbackTitle' }),
      content: (
        <>
          {intl.formatMessage({ id: 'importers.rollbackConfirm' }, { version: selectedVersion?.label })}
        </>
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.rollback' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: confirmRollback,
    })
  }

  const status =
    selected && selected !== 'all' && selectedVersion?.file ? (
      selectedVersion.file.status === 'failed' ? (
        <Warning />
      ) : (
        <Success />
      )
    ) : null

  const tempActionsMenu = [
    {
      value: 'rollback',
      label: intl.formatMessage({ id: 'forms.rollback' }),
      disabled: disabledRollback,
    },
    {
      value: 'logs',
      label: intl.formatMessage({ id: 'forms.logs' }),
      disabled: disabledDownloadLogs,
      statusSvg: status,
    },
  ]

  const actionsMenu =
    uploadState?.file_type === 'sales_codes' && selectedVersion?.value !== 'all'
      ? tempActionsMenu
      : [
          ...tempActionsMenu,
          {
            value: 'download',
            label: importFileId ? intl.formatMessage({ id: 'importers.downloadDiff' }) : intl.formatMessage({ id: 'forms.download' }),
            disabled: disabledDownload,
          },
        ]

  const finalActions =
    uploadState?.file_type !== 'dtcs' || importFileId === ''
      ? actionsMenu
      : [...actionsMenu, MissingLogOption]

  const menuItems = finalActions.map((item) => ({
    key: item.value,
    disabled: item.disabled,
    label: (
      <Flex justify="space-between" align="center">
        <span data-testid="importer-action" data-action={item.value}>
          {item.labelId ? intl.formatMessage({ id: item.labelId }) : item.label}
        </span>
        {item.statusSvg}
      </Flex>
    ),
  }))

  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'download':
        downloadPath()
        break

      case 'logs':
        downloadLogs()
        break

      case 'rollback':
        showRollbackConfirm()
        break

      case 'log_missing_components':
        downloadMissingComponent()
        break

      default:
        break
    }
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottom"
        disabled={disabled}
        overlayClassName="actions-dropdown"
        menu={{
          items: menuItems,
          onClick: handleMenuClick,
        }}
      >
        <Button className="actions-select-trigger" data-testid="importer-actions">
          <span>{intl.formatMessage({ id: 'generic.actions' })}</span>
          <DownOutlined />
        </Button>
      </Dropdown>

      {contextHolder}
    </>
  )
}

DropdownActions.propTypes = {
  fileType: PropTypes.string.isRequired,
  uploadState: PropTypes.object,
  reloadData: PropTypes.func.isRequired,
}

export default DropdownActions
